import { useEffect, useMemo, useRef, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { Item, ItemActions, ItemContent, ItemDescription, ItemTitle, ItemList } from "@/components/ui/item";
import { PageNav } from "@/components/ui/page-nav";
import { WritingStyleFrame } from "./WritingStyleFrame";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { TypeToConfirm } from "@/components/ui/type-to-confirm";
import { Badge } from "@/components/ui/badge";
import {
  Building2Icon,
  LogOutIcon,
  MailIcon,
  MoreVerticalIcon,
  Save,
  SendIcon,
  Trash2Icon,
  UploadIcon,
  User2Icon,
  UserMinusIcon,
  UserPlusIcon,
} from "lucide-react";
import {
  useClerk,
  useOrganization,
  useReverification,
  useUser,
} from "@clerk/react";
import type {
  OrganizationInvitationResource,
  OrganizationMembershipResource,
  OrganizationResource,
  RoleResource,
} from "@clerk/shared/types";
import { useDashboardStore } from "@/stores";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Frame,
  FrameHeader,
  FrameTitle,
  FramePanel,
  FrameFooter,
  FrameAction,
  FrameCount,
} from "@/components/ui/frame";
import { ScopeWrapper } from "@/components/ui/scope-wrapper";
import { clerkErrorMessage, toastClerkError } from "@/lib/clerk-errors";

const PAGE_SIZE = 10;
// What Clerk accepts for an organization logo.
const LOGO_TYPES = "image/png,image/jpeg,image/gif,image/webp";
const LOGO_MAX_BYTES = 10 * 1024 * 1024;
const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

function memberName(member: OrganizationMembershipResource) {
  const data = member.publicUserData;
  const full = [data?.firstName, data?.lastName].filter(Boolean).join(" ");
  return full || data?.identifier || "Unknown user";
}

/**
 * Organization settings, built on Clerk's frontend SDK (useOrganization +
 * the organization/membership resources) instead of Clerk's prebuilt
 * <OrganizationProfile />. Every action is gated on the caller's Clerk
 * system permissions, and Clerk enforces the same rules server-side.
 */
export function OrganizationSection() {
  const { organization, membership, isLoaded } = useOrganization();

  const permissions = membership?.permissions ?? [];
  const canManageProfile = permissions.includes("org:sys_profile:manage");
  const canDeleteOrg =
    permissions.includes("org:sys_profile:delete") &&
    !!organization?.adminDeleteEnabled;
  const canManageMembers = permissions.includes("org:sys_memberships:manage");
  const canReadMembers =
    canManageMembers || permissions.includes("org:sys_memberships:read");
  // Mount the Clerk-backed frames only once the membership is known, so the
  // permission-dependent hook params below are stable from the first render.
  const ready = isLoaded && !!organization && !!membership;

  return (
    <div className="space-y-8 pt-0 pb-10">
      {!ready || !organization ? (
        <Frame>
          <FrameHeader>
            <FrameTitle>Organization</FrameTitle>
          </FrameHeader>
          <FramePanel className="p-3">
            <Skeleton className="h-28 w-full rounded-lg" />
          </FramePanel>
        </Frame>
      ) : (
        <>
          <OrganizationProfileFrame
            organization={organization}
            canManage={canManageProfile}
          />
          {canReadMembers && (
            <OrganizationMembersFrames canManage={canManageMembers} />
          )}
        </>
      )}

      <WritingStyleFrame />

      {ready && organization && (
        <OrganizationDangerZone organization={organization} canDelete={canDeleteOrg} />
      )}
    </div>
  );
}

/** Logo + name. Read-only for members without org:sys_profile:manage. */
function OrganizationProfileFrame({
  organization,
  canManage,
}: {
  organization: OrganizationResource;
  canManage: boolean;
}) {
  const [name, setName] = useState(organization.name);
  const [savingName, setSavingName] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setName(organization.name);
  }, [organization.name]);

  const trimmedName = name.trim();
  const nameChanged = trimmedName.length > 0 && trimmedName !== organization.name;

  const handleSaveName = async () => {
    if (!nameChanged || savingName) return;
    setSavingName(true);
    try {
      await organization.update({ name: trimmedName });
      toast.success("Organization name updated");
    } catch (error) {
      toast.error(clerkErrorMessage(error, "Failed to update the organization name"));
    } finally {
      setSavingName(false);
    }
  };

  const handleSetLogo = async (file: File | null) => {
    if (file && file.size > LOGO_MAX_BYTES) {
      toast.error("The logo must be 10 MB or smaller");
      return;
    }
    setLogoBusy(true);
    try {
      await organization.setLogo({ file });
      toast.success(file ? "Logo updated" : "Logo removed");
    } catch (error) {
      toast.error(clerkErrorMessage(error, "Failed to update the logo"));
    } finally {
      setLogoBusy(false);
    }
  };

  return (
    <Frame>
      <FrameHeader>
        <FrameTitle>Organization</FrameTitle>
      </FrameHeader>
      <FramePanel className="flex flex-col gap-5">
        <div className="flex items-center gap-4">
          <Avatar variant="square" className="size-14 rounded-lg">
            <AvatarImage src={organization.imageUrl} alt={`${organization.name} logo`} />
            <AvatarFallback>
              <Building2Icon className="size-5 text-muted-foreground" />
            </AvatarFallback>
          </Avatar>
          <div className="flex flex-col gap-1.5">
            {canManage && (
              <div className="flex items-center gap-1">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={logoBusy}
                  onClick={() => fileInputRef.current?.click()}
                >
                  {logoBusy ? <Spinner /> : <UploadIcon />}
                  {organization.hasImage ? "Replace logo" : "Upload logo"}
                </Button>
                {organization.hasImage && (
                  <Button
                    variant="destructive-ghost"
                    size="sm"
                    disabled={logoBusy}
                    onClick={() => void handleSetLogo(null)}
                  >
                    <Trash2Icon />
                    Remove
                  </Button>
                )}
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {canManage
                ? "PNG, JPG, GIF or WEBP, up to 10 MB. Square images look best."
                : "Only organization admins can change the logo."}
            </p>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept={LOGO_TYPES}
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) void handleSetLogo(file);
            }}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="organization_name">Name</Label>
          <div className="flex items-center gap-2">
            <Input
              id="organization_name"
              value={name}
              disabled={!canManage || savingName}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void handleSaveName();
                if (e.key === "Escape") setName(organization.name);
              }}
            />
            {nameChanged && (
              <Button
                onClick={() => void handleSaveName()}
                disabled={savingName}
                className="shrink-0 animate-in fade-in-0 slide-in-from-left-2 duration-200"
              >
                {savingName ? <Spinner /> : <Save />}
                Save
              </Button>
            )}
          </div>
        </div>
      </FramePanel>
    </Frame>
  );
}

/**
 * Members table + pending invitations. Invitations are only fetched for
 * callers with org:sys_memberships:manage — Clerk refuses them otherwise.
 */
function OrganizationMembersFrames({ canManage }: { canManage: boolean }) {
  const { user } = useUser();
  const { organization, memberships, invitations } = useOrganization({
    memberships: { pageSize: PAGE_SIZE, keepPreviousData: true },
    invitations: canManage
      ? { pageSize: PAGE_SIZE, keepPreviousData: true, status: ["pending"] }
      : undefined,
  });
  const [roles, setRoles] = useState<RoleResource[]>([]);

  const organizationId = organization?.id;
  useEffect(() => {
    if (!canManage || !organization) return;
    let cancelled = false;
    organization
      .getRoles({ pageSize: 20 })
      .then((res) => {
        if (!cancelled) setRoles(res.data);
      })
      .catch(() => {
        // The role picker falls back to a read-only badge.
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canManage, organizationId]);

  if (!organization) return null;

  const members = memberships?.data ?? [];
  const pending = invitations?.data ?? [];
  const pendingCount = invitations?.count ?? 0;
  const loadingMembers = !!memberships?.isLoading && members.length === 0;

  return (
    <>
      <Frame>
        <FrameHeader>
          <FrameTitle>
            Members <FrameCount>{memberships?.count ?? organization.membersCount}</FrameCount>
          </FrameTitle>
          <FrameAction>
            {canManage && (
              <InviteMembersDialog
                organization={organization}
                roles={roles}
                onInvited={() => invitations?.revalidate?.()}
              />
            )}
          </FrameAction>
        </FrameHeader>

        {loadingMembers ? (
          <FramePanel className="p-3">
            <Skeleton className="h-16 w-full rounded-lg" />
          </FramePanel>
        ) : (
          <Table variant="card">
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead className="w-28">Joined</TableHead>
                <TableHead className="w-40">Role</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {members.map((member) => (
                <MemberRow
                  key={member.id}
                  member={member}
                  isSelf={member.publicUserData?.userId === user?.id}
                  canManage={canManage}
                  roles={roles}
                  onChanged={() => memberships?.revalidate?.()}
                />
              ))}
            </TableBody>
          </Table>
        )}

        <Pager resource={memberships} />
      </Frame>

      {canManage && pendingCount > 0 && invitations && (
        <Frame>
          <FrameHeader>
            <FrameTitle>
              Pending invitations <FrameCount>{pendingCount}</FrameCount>
            </FrameTitle>
          </FrameHeader>
          <Table variant="card">
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead className="w-28">Invited</TableHead>
                <TableHead className="w-40">Role</TableHead>
                <TableHead className="w-24" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {pending.map((invitation) => (
                <InvitationRow
                  key={invitation.id}
                  invitation={invitation}
                  onRevoked={() => invitations.revalidate?.()}
                />
              ))}
            </TableBody>
          </Table>
          <Pager resource={invitations} />
        </Frame>
      )}
    </>
  );
}

/** Prev/next footer for a paginated useOrganization resource; hidden on one page. */
function Pager({
  resource,
}: {
  resource?: {
    page?: number;
    pageCount?: number;
    count?: number;
    isFetching?: boolean;
    fetchPrevious?: () => void;
    fetchNext?: () => void;
  } | null;
}) {
  const page = resource?.page ?? 1;
  const pageCount = resource?.pageCount ?? 0;
  const total = resource?.count ?? 0;
  const disabled = !!resource?.isFetching;
  if (pageCount <= 1) return null;

  return (
    <FrameFooter>
      <PageNav
        page={page}
        pageCount={pageCount}
        total={total}
        pageSize={PAGE_SIZE}
        onPrevious={() => resource?.fetchPrevious?.()}
        onNext={() => resource?.fetchNext?.()}
        disabled={disabled}
      />
    </FrameFooter>
  );
}

/**
 * One member. Admins get a role picker and a Remove action; your own row is
 * read-only (leaving lives in the danger zone, and Clerk refuses demoting or
 * removing the last admin either way).
 */
function MemberRow({
  member,
  isSelf,
  canManage,
  roles,
  onChanged,
}: {
  member: OrganizationMembershipResource;
  isSelf: boolean;
  canManage: boolean;
  roles: RoleResource[];
  onChanged: () => Promise<void> | undefined;
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState<"role" | "remove" | null>(null);
  const name = memberName(member);
  const email = member.publicUserData?.identifier;
  const editable = canManage && !isSelf;
  // Keep the current role selectable even if it isn't in the fetched list.
  const roleOptions = roles.some((r) => r.key === member.role)
    ? roles
    : [{ key: member.role, name: member.roleName } as RoleResource, ...roles];

  const handleRoleChange = async (role: string) => {
    if (role === member.role) return;
    setBusy("role");
    try {
      await member.update({ role });
      const roleName = roles.find((r) => r.key === role)?.name ?? role;
      toast.success(`${name} is now ${roleName}`);
      await onChanged();
    } catch (error) {
      toast.error(clerkErrorMessage(error, "Failed to change the role"));
    } finally {
      setBusy(null);
    }
  };

  const handleRemove = async () => {
    setBusy("remove");
    try {
      await member.destroy();
      toast.success(`Removed ${name} from the organization`);
      await onChanged();
    } catch (error) {
      toast.error(clerkErrorMessage(error, "Failed to remove the member"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <TableRow data-context-menu className="group">
      <TableCell>
        <div className="flex min-w-0 items-center gap-2">
          <Avatar variant="square">
            <AvatarImage src={member.publicUserData?.imageUrl} alt={name} />
            <AvatarFallback>
              <User2Icon className="size-4" />
            </AvatarFallback>
          </Avatar>
          <div className="grid min-w-0 leading-tight">
            <span className="flex items-center gap-1.5 truncate text-sm font-medium">
              {name}
              {isSelf && <Badge variant="flat">You</Badge>}
            </span>
            {email && email !== name && (
              <span className="truncate text-xs text-muted-foreground">{email}</span>
            )}
          </div>
        </div>
      </TableCell>
      <TableCell className="text-xs font-mono text-muted-foreground">
        {member.createdAt.toLocaleDateString()}
      </TableCell>
      <TableCell>
        {editable && roles.length > 0 ? (
          <Select
            value={member.role}
            onValueChange={(role) => void handleRoleChange(role)}
            disabled={busy !== null}
          >
            <ScopeWrapper>
              <SelectTrigger size="sm" className="h-8 w-36 text-xs border-transparent shadow-none group-hover:border-input group-hover:shadow-xs data-[state=open]:border-input">
                {busy === "role" ? (
                  <span className="flex items-center gap-2">
                    <Spinner className="size-3.5" />
                    Saving…
                  </span>
                ) : (
                  <SelectValue />
                )}
              </SelectTrigger>
            </ScopeWrapper>
            <SelectContent>
              {roleOptions.map((role) => (
                <SelectItem key={role.key} value={role.key}>
                  {role.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Badge variant="ghost">{member.roleName}</Badge>
        )}
      </TableCell>
      <TableCell>
        {editable && (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="muted-ghost"
                  size="icon-sm"
                 
                  aria-label="Member options"
                  disabled={busy !== null}
                >
                  {busy === "remove" ? (
                    <Spinner className="size-3.5" />
                  ) : (
                    <MoreVerticalIcon className="size-4" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  variant="destructive"
                  onSelect={() => setConfirmOpen(true)}
                >
                  <UserMinusIcon />
                  Remove member
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
              <AlertDialogContent className="max-w-md">
                <AlertDialogHeader>
                  <AlertDialogTitle>Remove {name}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    They lose access to this organization and its campaigns
                    right away. Campaigns they own keep running. Invite them
                    again to give access back.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => void handleRemove()}
                    variant="destructive"
                  >
                    <UserMinusIcon />
                    Remove
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}

function InvitationRow({
  invitation,
  onRevoked,
}: {
  invitation: OrganizationInvitationResource;
  onRevoked: () => Promise<void> | undefined;
}) {
  const [revoking, setRevoking] = useState(false);

  const handleRevoke = async () => {
    setRevoking(true);
    try {
      await invitation.revoke();
      toast.success(`Revoked the invitation to ${invitation.emailAddress}`);
      await onRevoked();
    } catch (error) {
      toast.error(clerkErrorMessage(error, "Failed to revoke the invitation"));
    } finally {
      setRevoking(false);
    }
  };

  return (
    <TableRow>
      <TableCell>
        <div className="flex min-w-0 items-center gap-2">
          <div className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted [&_svg]:text-muted-foreground">
            <MailIcon className="size-3.5" />
          </div>
          <span className="truncate text-sm leading-normal">
            {invitation.emailAddress}
          </span>
        </div>
      </TableCell>
      <TableCell className="text-xs font-mono text-muted-foreground">
        {invitation.createdAt.toLocaleDateString()}
      </TableCell>
      <TableCell>
        <Badge variant="ghost">{invitation.roleName}</Badge>
      </TableCell>
      <TableCell>
        <div className="flex justify-end">
          <Button
            variant="destructive-ghost"
            size="xs"
            disabled={revoking}
            onClick={() => void handleRevoke()}
          >
            {revoking ? <Spinner /> : <Trash2Icon />}
            Revoke
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

/** Invite one or more people by email with a single role. */
function InviteMembersDialog({
  organization,
  roles,
  onInvited,
}: {
  organization: OrganizationResource;
  roles: RoleResource[];
  onInvited: () => Promise<void> | undefined;
}) {
  const defaultRole =
    roles.find((r) => r.key === "org:member")?.key ?? roles[0]?.key ?? "org:member";
  const [open, setOpen] = useState(false);
  const [emailsText, setEmailsText] = useState("");
  const [role, setRole] = useState(defaultRole);
  const [sending, setSending] = useState(false);

  const emails = useMemo(
    () => [
      ...new Set(
        emailsText
          .split(/[\s,;]+/)
          .map((e) => e.trim().toLowerCase())
          .filter(Boolean),
      ),
    ],
    [emailsText],
  );
  const invalid = emails.filter((e) => !EMAIL_RE.test(e));
  const canSend = emails.length > 0 && invalid.length === 0 && !sending;

  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      await organization.inviteMembers({ emailAddresses: emails, role });
      toast.success(
        emails.length === 1
          ? `Invitation sent to ${emails[0]}`
          : `${emails.length} invitations sent`,
      );
      setOpen(false);
      await onInvited();
    } catch (error) {
      toast.error(clerkErrorMessage(error, "Failed to send the invitations"));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nowOpen) => {
        setOpen(nowOpen);
        // Reset on open, not close, so the form doesn't blank while fading out.
        if (nowOpen) {
          setEmailsText("");
          setRole(defaultRole);
        }
      }}
    >
      <DialogTrigger asChild>
        <Button size="sm" className="shrink-0">
          <UserPlusIcon />
          Invite
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite members</DialogTitle>
          <DialogDescription>
            They get an email with a link to join {organization.name}.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <Label htmlFor="invite_emails">Email addresses</Label>
          <Textarea
            id="invite_emails"
            className="min-h-20 resize-none"
            placeholder="name@company.com, another@company.com"
            value={emailsText}
            disabled={sending}
            onChange={(e) => setEmailsText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void handleSend();
            }}
          />
          {invalid.length > 0 ? (
            <p className="text-xs text-destructive">
              Not a valid email: {invalid.join(", ")}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">
              Separate multiple emails with commas, spaces or new lines.
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Label>Role</Label>
          <Select value={role} onValueChange={setRole} disabled={sending}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a role" />
            </SelectTrigger>
            <SelectContent>
              {(roles.length ? roles : [{ key: "org:member", name: "Member" } as RoleResource]).map(
                (r) => (
                  <SelectItem key={r.key} value={r.key}>
                    {r.name}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="secondary" disabled={sending}>
              Cancel
            </Button>
          </DialogClose>
          <Button onClick={() => void handleSend()} disabled={!canSend}>
            {sending ? <Spinner /> : <SendIcon />}
            {emails.length > 1 ? `Send ${emails.length} invitations` : "Send invitation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Leave (any member) and delete (org:sys_profile:delete, when the Clerk
 * instance allows admin deletes). Both switch the session to another of the
 * user's organizations afterwards — or none — the same way the team
 * switcher does.
 */
function OrganizationDangerZone({
  organization,
  canDelete,
}: {
  organization: OrganizationResource;
  canDelete: boolean;
}) {
  const { user } = useUser();
  const { setActive } = useClerk();
  const { resetStore } = useDashboardStore();
  const navigate = useNavigate();
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmName, setConfirmName] = useState("");
  const [busy, setBusy] = useState<"leave" | "delete" | null>(null);
  // Sensitive actions: Clerk may ask the user to verify again first.
  const leaveOrganization = useReverification((organizationId: string) => {
    if (!user) throw new Error("not_logged_in");
    return user.leaveOrganization(organizationId);
  });
  const destroyOrganization = useReverification(() => organization.destroy());

  const moveToNextOrganization = async () => {
    const next = user?.organizationMemberships.find(
      (m) => m.organization.id !== organization.id,
    );
    await setActive({ organization: next?.organization.id ?? null });
    resetStore();
    navigate("/dashboard/");
  };

  const handleLeave = async () => {
    if (!user) return;
    setBusy("leave");
    try {
      await leaveOrganization(organization.id);
      toast.success(`You left ${organization.name}`);
      setLeaveOpen(false);
      await moveToNextOrganization();
    } catch (error) {
      toastClerkError(error, "Failed to leave the organization");
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async () => {
    if (confirmName.trim() !== organization.name) return;
    setBusy("delete");
    try {
      await destroyOrganization();
      toast.success(`Deleted ${organization.name}`);
      setDeleteOpen(false);
      await moveToNextOrganization();
    } catch (error) {
      toastClerkError(error, "Failed to delete the organization");
    } finally {
      setBusy(null);
    }
  };

  return (
    <Frame>
      <FrameHeader>
        <FrameTitle>Danger zone</FrameTitle>
      </FrameHeader>
      <ItemList className="rounded-lg">
        <Item variant="row">
          <ItemContent className="gap-0">
            <ItemTitle>Leave organization</ItemTitle>
            <ItemDescription>
              You lose access to its campaigns. An admin has to invite you again
              to rejoin.
            </ItemDescription>
          </ItemContent>
          <ItemActions>
            <Button
              variant="destructive-ghost"
              size="sm"
              disabled={busy !== null}
              onClick={() => setLeaveOpen(true)}
            >
              <LogOutIcon />
              Leave
            </Button>
          </ItemActions>
        </Item>
        {canDelete && (
          <Item variant="row">
            <ItemContent className="gap-0">
              <ItemTitle>Delete organization</ItemTitle>
              <ItemDescription>
                Removes the organization and every member's access to it.
              </ItemDescription>
            </ItemContent>
            <ItemActions>
              <Button
                variant="destructive"
                size="sm"
                disabled={busy !== null}
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2Icon />
                Delete
              </Button>
            </ItemActions>
          </Item>
        )}
      </ItemList>

      <AlertDialog open={leaveOpen} onOpenChange={setLeaveOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Leave {organization.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              You lose access to this organization right away. If you're its
              last admin, make someone else an admin first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy === "leave"}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={busy === "leave"}
              onClick={() => void handleLeave()}
            >
              {busy === "leave" ? <Spinner /> : <LogOutIcon />}
              Leave
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={deleteOpen}
        onOpenChange={(nowOpen) => {
          setDeleteOpen(nowOpen);
          if (nowOpen) setConfirmName("");
        }}
      >
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader variant="destructive">
            <AlertDialogTitle>Delete {organization.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Every member loses access immediately. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <TypeToConfirm
            phrase={organization.name}
            value={confirmName}
            onValueChange={setConfirmName}
            onEnter={() => void handleDelete()}
            disabled={busy === "delete"}
          />
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy === "delete"}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={busy === "delete" || confirmName.trim() !== organization.name}
              onClick={() => void handleDelete()}
            >
              {busy === "delete" ? <Spinner /> : <Trash2Icon />}
              Delete organization
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Frame>
  );
}
